import { CHAT_ERROR_CODES, QUOTA_ERROR_CODES } from '@shared/constants/error-codes';
import { extractChatErrorCode, getChatErrorMessage } from './chat-errors';

export type ChatTurnErrorKind = 'business' | 'network' | 'quota' | 'technical';

export type ChatTurnErrorState = {
  code: string | null;
  failedUserMessageId: string | null;
  hasPartialResponse: boolean;
  kind: ChatTurnErrorKind;
  message: string;
  retryable: boolean;
};

const NON_RETRYABLE_CODES = new Set<string>([
  CHAT_ERROR_CODES.QUOTA_EXCEEDED,
  QUOTA_ERROR_CODES.COMPANY_QUOTA_EXCEEDED,
  'AUTH_REQUIRED',
  'FORBIDDEN',
  'VALIDATION_ERROR',
]);

const EXPECTED_BUSINESS_CODES = new Set<string>([
  CHAT_ERROR_CODES.CHAT_ATTACHMENT_NOT_FOUND,
  CHAT_ERROR_CODES.CHAT_ATTACHMENT_NOT_READY,
  CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_LARGE,
  CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_MANY,
  CHAT_ERROR_CODES.CHAT_ATTACHMENT_UNSUPPORTED,
  CHAT_ERROR_CODES.CONVERSATION_NOT_FOUND,
  CHAT_ERROR_CODES.QUOTA_EXCEEDED,
  QUOTA_ERROR_CODES.COMPANY_QUOTA_EXCEEDED,
  CHAT_ERROR_CODES.RAG_NO_COMPANY,
  CHAT_ERROR_CODES.RAG_NO_INDEXED_DOCUMENTS,
  CHAT_ERROR_CODES.TOOL_CONTINUATION_ALREADY_APPLIED,
  'AUTH_REQUIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_ERROR',
]);

const NETWORK_INTERRUPTION_MESSAGE = 'La connexion au service a été interrompue. Vérifiez votre réseau puis réessayez.';

function isNetworkError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return error instanceof TypeError
    || message.includes('failed to fetch')
    || message.includes('network')
    || message.includes('connexion');
}

export function createChatTurnErrorState(params: {
  error: unknown;
  failedUserMessageId: string | null;
  hasPartialResponse: boolean;
}): ChatTurnErrorState {
  const code = extractChatErrorCode(params.error);
  const networkError = isNetworkError(params.error);
  const kind: ChatTurnErrorKind = code === CHAT_ERROR_CODES.QUOTA_EXCEEDED
    || code === QUOTA_ERROR_CODES.COMPANY_QUOTA_EXCEEDED
    ? 'quota'
    : networkError
      ? 'network'
      : code && EXPECTED_BUSINESS_CODES.has(code)
        ? 'business'
        : 'technical';

  return {
    code,
    failedUserMessageId: params.failedUserMessageId,
    hasPartialResponse: params.hasPartialResponse,
    kind,
    message: networkError
      ? NETWORK_INTERRUPTION_MESSAGE
      : getChatErrorMessage(params.error),
    retryable: !code || !NON_RETRYABLE_CODES.has(code),
  };
}

/**
 * Stream coupé en vol (perte de connexion SSE) sans Error remontée par `onError` :
 * `onFinish` signale `isDisconnect`. On matérialise alors une erreur de tour réseau
 * réessayable pour ne jamais laisser une réponse tronquée muette.
 */
export function createDisconnectTurnErrorState(params: {
  failedUserMessageId: string | null;
  hasPartialResponse: boolean;
}): ChatTurnErrorState {
  return {
    code: null,
    failedUserMessageId: params.failedUserMessageId,
    hasPartialResponse: params.hasPartialResponse,
    kind: 'network',
    message: NETWORK_INTERRUPTION_MESSAGE,
    retryable: true,
  };
}

export function shouldCaptureChatTurnError(turnError: ChatTurnErrorState) {
  return turnError.kind === 'network' || turnError.kind === 'technical';
}

export type ChatStreamOutcome =
  | { finishReason?: string; type: 'aborted' }
  | { finishReason?: string; type: 'disconnected' }
  | { finishReason?: string; type: 'error' }
  | { finishReason?: string; type: 'success' };

export function resolveChatStreamOutcome(params: {
  finishReason?: string;
  isAbort: boolean;
  isDisconnect: boolean;
  isError: boolean;
}): ChatStreamOutcome {
  if (params.isAbort) {
    return { finishReason: params.finishReason, type: 'aborted' };
  }

  if (params.isDisconnect) {
    return { finishReason: params.finishReason, type: 'disconnected' };
  }

  if (params.isError) {
    return { finishReason: params.finishReason, type: 'error' };
  }

  return { finishReason: params.finishReason, type: 'success' };
}
